import { useCallback, useState } from 'react';
import { Icon } from '@iconify-icon/react';

interface DocsCodeBlockProps {
  /** Raw code to render. Whitespace is preserved verbatim. */
  code: string;
  /** Short language label shown in the header chip, e.g. "bash", "ts", "json". */
  language?: string;
  /** Optional file name / path shown on the left of the header. */
  filename?: string;
  /** Hide the copy button (rare — only for illustrative, non-runnable snippets). */
  hideCopy?: boolean;
}

/**
 * The single code surface for every docs article.
 *
 * Before this existed each article hand-rolled `<div class="bg-gray-900"><pre>`,
 * which drifted (different paddings, no copy affordance, no language label, and
 * long lines silently overflowed the prose column on mobile). Everything that
 * shows code in /docs should go through here so the reader gets the same
 * header, the same scroll behaviour, and a working copy button every time.
 */
const DocsCodeBlock: React.FC<DocsCodeBlockProps> = ({
  code,
  language,
  filename,
  hideCopy = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Older browsers / insecure contexts have no clipboard API. Fall back to
      // the execCommand path so the button is never a dead control.
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [code]);

  return (
    <div className="not-prose my-6 overflow-hidden rounded-xl border border-slate-800 bg-slate-900 shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/80 px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {filename ? (
            <span className="truncate font-mono text-xs text-slate-300">{filename}</span>
          ) : (
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-slate-700" />
              <span className="h-2.5 w-2.5 rounded-full bg-slate-700" />
              <span className="h-2.5 w-2.5 rounded-full bg-slate-700" />
            </span>
          )}
          {language && (
            <span className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-slate-400">
              {language}
            </span>
          )}
        </div>
        {!hideCopy && (
          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? 'Copied to clipboard' : 'Copy code to clipboard'}
            className="flex flex-shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100">
            <Icon icon={copied ? 'tabler:check' : 'tabler:copy'} className="text-sm" />
            {copied ? 'Copied' : 'Copy'}
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <pre className="!m-0 !bg-transparent px-4 py-4 font-mono text-[13px] leading-relaxed text-slate-200">
          {code}
        </pre>
      </div>
    </div>
  );
};

export default DocsCodeBlock;
